import { authService } from './auth';
import { BUG_REPORT_CONFIG } from '../config/bugReport';

export interface BugReportData {
  message: string;
  user: {
    name: string;
    email: string;
  } | null;
  timestamp: string;
  userAgent: string;
  url: string;
}

class BugReportService {
  // Option 1: Webhook.site (for testing)
  private async sendToWebhook(data: BugReportData): Promise<boolean> {
    if (!BUG_REPORT_CONFIG.WEBHOOK.ENABLED) return false;
    
    try {
      const response = await fetch(BUG_REPORT_CONFIG.WEBHOOK.URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      return response.ok;
    } catch (error) {
      console.error('Webhook submission failed:', error);
      return false;
    }
  }

  // Option 2: Email via EmailJS (free tier available)
  private async sendViaEmailJS(data: BugReportData): Promise<boolean> {
    try {
      // You'll need to add EmailJS to your project
      // npm install @emailjs/browser
      // Then configure it with your EmailJS account
      const emailData = {
        to_email: 'your-email@example.com',
        from_name: data.user?.name || 'Anonymous User',
        from_email: data.user?.email || 'anonymous@example.com',
        message: data.message,
        user_agent: data.userAgent,
        url: data.url,
        timestamp: data.timestamp
      };

      // This is a placeholder - you'd need to set up EmailJS
      console.log('EmailJS would send:', emailData);
      return true;
    } catch (error) {
      console.error('EmailJS submission failed:', error);
      return false;
    }
  }

  // Option 3: Google Forms (easiest to set up)
  private async sendToGoogleForms(data: BugReportData): Promise<boolean> {
    if (!BUG_REPORT_CONFIG.GOOGLE_FORMS.ENABLED) return false;
    
    try {
      const formData = new FormData();
      formData.append(BUG_REPORT_CONFIG.GOOGLE_FORMS.FIELD_IDS.MESSAGE, data.message);
      formData.append(BUG_REPORT_CONFIG.GOOGLE_FORMS.FIELD_IDS.USER_NAME, data.user?.name || 'Anonymous');
      formData.append(BUG_REPORT_CONFIG.GOOGLE_FORMS.FIELD_IDS.USER_EMAIL, data.user?.email || 'anonymous@example.com');
      formData.append(BUG_REPORT_CONFIG.GOOGLE_FORMS.FIELD_IDS.URL, data.url);
      formData.append(BUG_REPORT_CONFIG.GOOGLE_FORMS.FIELD_IDS.TIMESTAMP, data.timestamp);

      // Try the fetch with better error handling
      const response = await fetch(BUG_REPORT_CONFIG.GOOGLE_FORMS.FORM_URL, {
        method: 'POST',
        body: formData,
        mode: 'no-cors', // Required for Google Forms
      });

      // With no-cors, we can't read the response, but we can check if the request was made
      console.log('Google Forms submission attempted');
      return true; // Assume success since we can't read the response with no-cors
    } catch (error) {
      console.error('Google Forms submission failed:', error);
      
      // If Google Forms fails, try the mailto fallback immediately
      const mailtoLink = this.createMailtoLink(data);
      window.open(mailtoLink, '_blank');
      
      return false;
    }
  }

  // Option 4: Simple email via mailto (fallback)
  private createMailtoLink(data: BugReportData): string {
    const subject = encodeURIComponent('Bug Report - PatternLab Dashboard');
    const body = encodeURIComponent(`
Bug Report Details:

Message:
${data.message}

User: ${data.user?.name || 'Anonymous'} (${data.user?.email || 'No email'})
URL: ${data.url}
Timestamp: ${data.timestamp}
User Agent: ${data.userAgent}
    `);

    return `mailto:your-email@example.com?subject=${subject}&body=${body}`;
  }

  async submitBugReport(message: string): Promise<{ success: boolean; method: string }> {
    const user = authService.getCurrentUser();
    const data: BugReportData = {
      message: message.trim(),
      user: user ? {
        name: user.name,
        email: user.email
      } : null,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Try different methods in order of preference
    const methods = [
      { name: 'Google Forms', fn: () => this.sendToGoogleForms(data) },
      { name: 'Webhook', fn: () => this.sendToWebhook(data) },
      { name: 'EmailJS', fn: () => this.sendViaEmailJS(data) }
    ];

    for (const method of methods) {
      try {
        const success = await method.fn();
        if (success) {
          return { success: true, method: method.name };
        }
      } catch (error) {
        console.error(`${method.name} failed:`, error);
      }
    }

    // Fallback to mailto link
    const mailtoLink = this.createMailtoLink(data);
    window.open(mailtoLink, '_blank');
    
    return { success: true, method: 'Email (mailto)' };
  }
}

export const bugReportService = new BugReportService();
